import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { AccessibilityWidget } from "@/components/AccessibilityWidget";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Phone, ChevronDown, CheckCircle2 } from "lucide-react";

import heroImage from "@/assets/services/concrete-foundations-lincoln-park-nj.webp";
import soilImage from "@/assets/concrete-foundation-prep-paramus-bergen-county-nj.webp";
import materialsImage from "@/assets/concrete-foundation-installation-englewood-bergen-county-nj.webp";

const processSteps = [
  {
    title: "Site Visit",
    description:
      "Our foundation installation process begins with a site visit with either Biagio or Gino. This is a free service and we do not charge for this step. We review the soil conditions, the drainage patterns, and the plans for your home.",
  },
  {
    title: "Written Estimate",
    description:
      "From there, we talk about an estimate before any work begins. We do not give verbal ballpark figures.",
  },
  {
    title: "Excavation & Footings",
    description:
      "Using the building plans to outline the foundation, we dig to the correct depth and begin with the footings. We pour the concrete, adding rebar for strength.",
  },
  {
    title: "Foundation Walls",
    description:
      "Once we have the footings set, we can begin on the foundation walls. Every wall is formed and poured to match your home design and site conditions.",
  },
  {
    title: "Waterproofing & Drainage",
    description:
      "We never skip waterproofing and drainage boards. Water will find its way into your foundation. It's not a matter of if, but when.",
  },
  {
    title: "Backfill & Grading",
    description:
      "Backfill is then packed down with the ground sloping away from the building on all sides.",
  },
  {
    title: "Final Walkthrough",
    description:
      "Once the job is completed, Gino will walk the project with you so you know what was built and why each decision was made.",
  },
];

const faqs = [
  {
    question: "Does a new foundation require a permit in New Jersey?",
    answer:
      "Yes, a new foundation requires a permit under the New Jersey Uniform Construction Code.³ Inspections are required at certain stages of the process, usually before the footings are poured and again before walls are backfilled. We pull the permits and schedule inspections. You do not have to manage that.",
  },
  {
    question: "How deep do footings need to be?",
    answer:
      "Footings should be between 36\" to 42\" below the frost line. The soil conditions or past frost records can also affect how deep the footings need to go. We verify the required depth for your specific location before we form the footings.",
  },
  {
    question:
      "What is the difference between a poured concrete foundation and a block foundation?",
    answer:
    "A poured concrete foundation is built as one continuous wall. It has no seams or joints that can crack or where water can seep in. A block foundation is built with mortar joints, and every joint is a potential water entry point and a location where cracking can develop over time. For our climate, we prefer a continuous wall as they perform better and are stronger for our North Jersey seasons.",
  },
  {
    question: "How long does a concrete foundation installation take?",
    answer:
      "For a typical home foundation in North Jersey, from first dig to backfill, runs two to four weeks. This includes footing and wall forming, pouring the concrete and cure time, waterproofing, and drainage installation. Permit turnaround, inspection scheduling, and weather all affect the timeline. We give you a realistic schedule at the start of the job, not an optimistic one.",
  },
  {
    question: "Can you pour a foundation in cold weather?",
    answer:
      "Yes, we can and we have. Using the correct materials make all the difference. ACI 306R cold weather concreting guidelines cover cold weather scenarios.⁴ When temperatures drop below 40°F, concrete must be protected from freezing during the curing period. We use insulating blankets and, when necessary, heated enclosures to maintain proper curing conditions. We do not pour when conditions put the work at risk.",
  },
  {
    question: "Do I need waterproofing on a new foundation?",
    answer:
      "Yes. Here's why. Clay soils hold rainwater against the foundation walls for extended periods of time. High water tables in low-lying areas create water pressure that pushes water through any gap in the wall. Exterior waterproofing and drainage board applied before backfill is not something we skip on a new pour in this region. They are what separates a dry basement from one that has problems within the first five years.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

const sources = [
  {
    num: 1,
    text: "USDA Natural Resources Conservation Service — New Jersey Soil Survey — Clay soil composition and expansion characteristics across North Jersey counties",
  },
  {
    num: 2,
    text: "ASTM C666/C666M — Standard Test Method for Resistance of Concrete to Rapid Freezing and Thawing — Concrete mix specifications for freeze-thaw exposure climates",
  },
  {
    num: 3,
    text: "New Jersey Department of Community Affairs — New Jersey Uniform Construction Code — Permit requirements for structural and foundation work in New Jersey municipalities",
  },
  {
    num: 4,
    text: "ACI 306R — Guide to Cold Weather Concreting — Temperature thresholds, mix adjustments, and protection requirements for concrete placed in cold conditions",
  },
];

export default function Foundations() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Concrete Foundations Totowa, NJ | Masonry Pro Construction</title>
        <meta property="og:description" content="New concrete foundations poured in Totowa and across North Jersey. 35+ years of union-trained experience serving Bergen, Essex, Passaic, and Morris Counties." />
        <meta property="og:title" content="Concrete Foundations Totowa, NJ | Masonry Pro Construction" />
        <meta
          name="description"
          content="New concrete foundations poured in Totowa and across North Jersey. 35+ years of union-trained experience serving Bergen, Essex, Passaic, and Morris Counties."
        />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Concrete Foundations",
            description: "New concrete foundation installation for custom homes and additions across North Jersey.",
            url: "/concrete-services/foundations",
            image: "https://masonry-pro.com/og/concrete-foundations.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/concrete-foundations.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/concrete-foundations.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Concrete foundation installation in Lincoln Park, Morris County, NJ" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 text-center relative z-10">
              <div className="max-w-3xl mx-auto">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Concrete Foundations in{" "}
                  <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Totowa, NJ</span> Serving North Jersey
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.9)', fontSize: '1.25rem', marginBottom: '2rem', maxWidth: '42rem', margin: '0 auto 2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  New concrete foundations for custom homes, additions, walkout basements, and outbuildings, built for North Jersey soil and weather.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button variant="gold" size="lg" asChild>
                    <a href="tel:+19737252395">
                      <Phone className="mr-2 h-5 w-5" />
                      (973) 725-2395
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10" asChild>
                    <Link to="/contact">Request a Free Estimate</Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Intro Section */}
          <section className="py-16 md:py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Concrete Foundation Installation:{" "}
                  <span className="text-gradient-gold">
                    A Masonry Pro Construction Specialty
                  </span>
                </h2>
                <div className="space-y-4 text-muted-foreground text-lg">
                  <p>
                    At Masonry Pro Construction, we have been installing concrete
                    foundations for over 35 years.
                  </p>
                  <p>
                    Biagio started as a union bricklayer and built his career in
                    the ground. Gino has spent 14 years in the field across
                    Bergen, Passaic, Essex, and Morris Counties. When you
                    hire us, one of them is on your job every day.
                  </p>
                  <p>
                    We install foundations for custom homes, additions, walkout
                    basements, and outbuildings. Every pour is sized to your
                    specific home design, your lot conditions, and the soil under
                    your feet.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* North Jersey Soil Section */}
          <section className="py-16 md:py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    North Jersey Soil Changes How a Foundation{" "}
                    <span className="text-gradient-gold">Has to Be Built</span>
                  </h2>
                  <div className="space-y-4 text-muted-foreground text-lg">
                    <p>
                      North Jersey ground is a mix of clay, bedrock, rock, sand,
                      and dirt. During our seasonal weather, our particular soil
                      mixes with water, expanding significantly and then
                      contracting during the dry periods.¹ Foundation walls can
                      shift and footings pushed out of place when foundations are
                      not properly poured and placed.
                    </p>
                    <p>
                      We don't guess at what's underground. That is why every new
                      foundation project begins with a site assessment that looks
                      at soil conditions, drainage patterns, slope of the
                      property, and soil reports.
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border border-border aspect-[4/3]">
                  <img
                    src={soilImage}
                    alt="Work boot pushing a shovel into the earth for concrete foundation prep in Paramus, Bergen County, NJ, by Masonry Pro"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Winters / Materials Section */}
          <section className="py-16 md:py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                <div className="rounded-xl overflow-hidden border border-border aspect-[4/3] order-2 md:order-1">
                  <img
                    src={materialsImage}
                    alt="Concrete foundation installation in progress with a worker in Englewood, Bergen County, NJ, by Masonry Pro Construction."
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="order-1 md:order-2">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Winters in North Jersey Require{" "}
                    <span className="text-gradient-gold">
                      a Different Type of Material
                    </span>
                  </h2>
                  <div className="space-y-4 text-muted-foreground text-lg">
                    <p>
                      North Jersey winters are hard on concrete. Not using the
                      right material could cause the concrete to crumble faster
                      than it should.
                    </p>
                    <p>
                      Biagio learned what works and what fails through decades of
                      work. He doesn't choose materials based on cost. He chooses
                      what lasts.
                    </p>
                    <p>
                      That's why we use concrete that endures rapid freezing and
                      thawing (ASTM C666).² This type of concrete has tiny air
                      pockets that give frozen water room to expand, which is how
                      concrete survives freeze-thaw cycles without cracking. Even
                      better, it's built for North Jersey winters.
                    </p>
                    <p>
                      When we pour a foundation, we use less water in the mix,
                      keeping the concrete stronger which makes it last longer.
                      Steel rebar is used to help the walls resist pressure from
                      soil pushing against them. We pick the system that fits
                      your home design and keeps your basement warm, so we may
                      use insulated blocks. And we don't forget the waterproof
                      protection that keeps groundwater away from the walls
                      before we backfill.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Foundation Types Section */}
          <section className="py-16 md:py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  A Specific Foundation for{" "}
                  <span className="text-gradient-gold">
                    Your Specific Needs
                  </span>
                </h2>
                <div className="space-y-4 text-muted-foreground text-lg">
                  <p>
                    You know not every house is the same. It makes sense that not
                    every foundation is the same, either. The type of structure
                    you are building, the slope of your lot, and the soil
                    conditions all factor into which foundation system is right
                    for your project.
                  </p>
                  <p>We handle:</p>
                  <ul className="space-y-3 mt-4">
                    {[
                      "Full and walkout basement foundations",
                      "Slab-on-grade foundations",
                      "Insulated Concrete Form (ICF) foundations for energy-efficient needs",
                      "Pier-and-beam foundations",
                    ].map((item, i) => (
                      <li key={i} className="flex gap-3">
                        <CheckCircle2 className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4">
                    We've seen a lot of homes that don't solve your problem
                    because the foundations are not installed correctly. As
                    masonry professionals we know how to build your foundation
                    right from the start.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Process Steps */}
          <section className="py-16 md:py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                  Start to Finish:{" "}
                  <span className="text-gradient-gold">
                    The Steps We Take
                  </span>
                </h2>
                <p className="text-muted-foreground text-lg text-center mb-10">
                  Most residential pours for custom homes take two to four
                  weeks.
                </p>
                <div className="space-y-6">
                  {processSteps.map((step, i) => (
                    <div
                      key={step.title}
                      className="flex gap-5 p-6 rounded-xl bg-card border border-border"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                        <span className="text-secondary font-bold">
                          {i + 1}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-1">
                          {step.title}
                        </h3>
                        <p className="text-muted-foreground">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Cost Section */}
          <section className="py-16 md:py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Cost of a{" "}
                  <span className="text-gradient-gold">New Foundation</span>
                </h2>
                <div className="space-y-4 text-muted-foreground text-lg">
                  <p>
                    The type and size of the foundation determine different
                    materials and labor costs. If the ground contains heavy clay
                    deposits or the soil is loose and unstable, these all carry
                    different ways in which we approach the solution. Engineering
                    permits may need to be obtained which would also factor into
                    your cost.
                  </p>
                  <p>
                    We give you a written, itemized estimate before any work
                    begins. Biagio or Gino will walk the site with you, go
                    through what they found, and give you a number that reflects
                    what is actually going in the ground.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Category hub backlink */}
          <section className="py-8 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <p className="text-muted-foreground">
                  New foundations sit alongside the rest of our <Link to="/concrete-services" className="text-secondary hover:underline">North Jersey concrete services</Link>, from footings to flatwork.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-16 md:py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center">
                  Concrete Foundation{" "}
                  <span className="text-gradient-gold">FAQs</span>
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {faqs.map((faq) => (
                    <div
                      key={faq.question}
                      className="bg-card rounded-xl p-6 border border-border"
                    >
                      <h3 className="text-lg font-bold mb-3">
                        {faq.question}
                      </h3>
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Citations */}
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      Citations &amp; References
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
                        {sources.map((s) => (
                          <li key={s.num}>{s.text}</li>
                        ))}
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="concrete" />

          <CTASection />
        </main>

        <Footer />
        <AccessibilityWidget />
      </div>
    </>
  );
}
